import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LuxonModule } from '@modules/luxon';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconButton } from '@angular/material/button';
import { Store } from '@ngrx/store';
import { selectGlobalFiltersState } from '@app/store/reducers';
import { setGlobalFilters } from '@app/store/global-filter.actions';
import { MatCardModule } from '@angular/material/card';
import { LuxonDateRangePipe } from '@modules/luxon/pipes/luxon-date-range.pipe';
import {
  ConnectionMonitorService,
  ConnectionQuality,
} from '@modules/connection-monitor/services/connection-monitor.service';
import { Dictionary } from '@ngrx/entity';
import { stateViewerFeature } from '@modules/state-viewer/state-viewer.reducer';
import { AsyncPipe } from '@angular/common';
import { IfHasPrivilegesDirective } from '@modules/auth-utils/directives/if-has-privileges.directive';
import type { StateViewerDialogComponent } from '@modules/state-viewer/state-viewer-dialog.component';
import { GlobalDateFilterDialogService } from '@app/core/components/global-date-filter-dialog/global-date-filter-dialog.service';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';

interface Status {
  color: string;
  iconName: string;
}

@UntilDestroy()
@Component({
    selector: 'app-status-bar',
    templateUrl: './status-bar.component.html',
    styleUrls: ['./status-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [LuxonModule, MatIconModule, MatDialogModule, MatCardModule, LuxonDateRangePipe, AsyncPipe, MatIconButton, IfHasPrivilegesDirective]
})
export class StatusBarComponent implements OnInit {
  protected fb = inject(FormBuilder);
  protected connectionMonitorService = inject(ConnectionMonitorService);

  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly globalDateFilterDialogService = inject(GlobalDateFilterDialogService);
  protected readonly stateViewerPreviouslyOpened$ = this.store.select(stateViewerFeature.selectOpened);
  protected readonly selectedTenant = this.store.selectSignal(userTenantFeature.selectSelectedTenant);
  protected readonly userHasAccessToMoreThanOneTenant =
    this.store.selectSignal(userTenantFeature.selectUserHasAccessToMoreThanOneTenant);

  form: FormGroup;
  statusMap: Dictionary<Status> = {
    'No Connection': { color: '#f44336', iconName: 'wifi_bad' },
    'Gathering Data': { color: '#3f51b5', iconName: 'network_check'},
    Excellent: { color: '#3f51b5', iconName: 'wifi'},
    Good: { color: '#3f51b5', iconName: 'wifi'},
    Mediocre: { color: '#3f51b5', iconName: 'wifi_2_bar'},
    Bad: { color: '#3f51b5', iconName: 'wifi_1_bar'},
    Terrible: { color: '#3f51b5', iconName: 'wifi_1_bar'},
  };
  connectionQuality: ConnectionQuality = ConnectionQuality.GATHERING_DATA;
  shouldFadeIn = false;

  constructor() {
    const fb = this.fb;

    this.form = fb.group({
      start: null,
      end: null,
    });

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(formValue =>
        this.store.dispatch(setGlobalFilters({
          startAtOrAfter: formValue.start,
          endAtOrBefore: formValue.end,
        })),
      );
  }

  ngOnInit() {
    this.store.select(selectGlobalFiltersState).pipe(

      untilDestroyed(this),
    ).subscribe((globalFilters) => {
        const startAtOrAfter = globalFilters?.startAtOrAfter ?? null;
        const endAtOrBefore = globalFilters?.endAtOrBefore ?? null;

        if (startAtOrAfter !== this.form.value.start || endAtOrBefore !== this.form.value.end) {
          this.form.setValue({start: startAtOrAfter, end: endAtOrBefore}, {emitEvent: false});
        }
      });

    this.connectionMonitorService.connectionQuality$
      .pipe(untilDestroyed(this))
      .subscribe((connectionQuality) => {
        this.connectionQuality = connectionQuality;
        this.shouldFadeIn = true;
        setTimeout(() => {
          this.shouldFadeIn = false;
        }, 500);
      });
  }

  onDateRangeClick() {
    void this.globalDateFilterDialogService.open();
  }

  protected async showStateViewerDialog() {
    const { StateViewerDialogComponent } = await import('@modules/state-viewer/state-viewer-dialog.component');
    this.dialog.open<StateViewerDialogComponent>(StateViewerDialogComponent, {
      minWidth: '85vw',
      maxHeight: '100vh',
    });
  }
}
